/**
 * Taskpane Command Handlers
 * Extracted handlers for taskpane UI commands.
 */

/* global document, Office, OfficeRuntime */

import { signIn as authSignIn, signOut as authSignOut } from "../shared/auth/authController";
import { AuthDialogError } from "../shared/auth/dialogAuth";
import { refreshAccountUI, showOtpFallback, setEntitlementUI } from "./ui/account";
import {
  ping,
  testAuth,
  getProxyBaseUrlInUse,
  createCheckoutSession,
  createBillingPortalSession,
  ProxyClientError,
} from "../services/proxyClient";
import {
  refreshEntitlements as refreshEntitlementsFromService,
  getEntitlements,
  getCachedEntitlements,
  getEffectiveLimits,
  clearEntitlementsCache,
} from "../services/entitlements";
import { authStore } from "../shared/auth";
import { writeTable } from "../excel/tables";
import { STORAGE_KEYS } from "../shared/constants";
import { logger } from "../shared/logger";
import { toExcelDate, ExcelNumberFormats } from "../shared/datetime";
import type { StoredPingResult } from "../shared/types";
import { buildOccSymbol, parseOccSymbol } from "../shared/symbols/occOptionSymbol";
import { normalizeUnderlying, normalizeSide } from "../shared/symbols/occOptionSymbol";
import { coerceExcelDateToYyyyMmDd, coerceExcelNumber } from "../shared/excel/normalize";
import { buildOptionInsertFormula } from "../excel/optionInsertFormula";
import { runExcel } from "../excel/runExcel";
import { fill2d } from "../excel/writeEntityToSelection";
import { UserInputError } from "../shared/errors/UserInputError";
import { refreshSelectedOptionsAction } from "./actions/refreshSelectedOptions";
import { refreshSheetOptionsAction } from "./actions/refreshSheetOptions";
import { setStatusMessage, clearStatusMessage } from "./ui/status";
import { buildOptionChain } from "../excel/chains/buildOptionChain";
import { insertOptionChain, BATCH_SIZE } from "../excel/chains/insertOptionChain";
import { DEFAULT_CHAIN_FIELD_KEYS } from "../excel/chains/chainFields";
import { generateStrikes } from "../excel/chains/buildOptionChain";
import type { ChainContract } from "../excel/chains/buildOptionChain";
import { diagnosticsStore } from "../shared/diagnostics/diagnosticsStore";
import { getOptionSnapshot } from "../services/massive";
import { NotListedError } from "../shared/errors/notListedError";
import {
  showPlanLimitDialog,
  wasPlanLimitDialogShownThisSession,
  setPlanLimitDialogShownThisSession,
} from "./ui/upgradeDialog";
import {
  markStripeFlowStarted,
  markStripeFlowHandled,
  recordStripeRefresh,
  isStripeFlowHandled,
  getMillisSinceLastStripeRefresh,
} from "./stripeReturnCoordinator";
import {
  isPlanLimitError,
  getPlanLimitInfo,
  type PlanLimitInfo,
} from "../domain/plan/planLimitUi";

const UPGRADE_WALL_DEBOUNCE_MS = 10_000;
const UPGRADE_WALL_DISMISS_TTL_MS = 30 * 60 * 1000;

const lastUpgradeWallShownAt = new Map<string, number>();

function upgradeWallKey(limitKey: string, operation: string): string {
  return (limitKey || "unknown") + ":" + (operation || "unknown");
}

async function isUpgradeWallDismissed(limitKey: string, operation: string): Promise<boolean> {
  try {
    const storage = typeof OfficeRuntime !== "undefined" ? OfficeRuntime.storage : null;
    if (!storage) return false;
    const key = STORAGE_KEYS.UPGRADE_WALL_DISMISSED_PREFIX + upgradeWallKey(limitKey, operation);
    const raw = await storage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw as string) as { at?: number };
    const at = typeof parsed?.at === "number" ? parsed.at : 0;
    return Date.now() - at < UPGRADE_WALL_DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

async function setUpgradeWallDismissed(limitKey: string, operation: string): Promise<void> {
  try {
    const storage = typeof OfficeRuntime !== "undefined" ? OfficeRuntime.storage : null;
    if (!storage) return;
    const key = STORAGE_KEYS.UPGRADE_WALL_DISMISSED_PREFIX + upgradeWallKey(limitKey, operation);
    await storage.setItem(key, JSON.stringify({ at: Date.now() }));
  } catch {
    // ignore
  }
}

function shouldShowUpgradeWall(info: PlanLimitInfo, operation: string): boolean {
  const key = upgradeWallKey(info.limit_key ?? "unknown", operation);
  const last = lastUpgradeWallShownAt.get(key);
  if (last != null && Date.now() - last < UPGRADE_WALL_DEBOUNCE_MS) return false;
  return true;
}

function recordUpgradeWallShown(info: PlanLimitInfo, operation: string): void {
  const key = upgradeWallKey(info.limit_key ?? "unknown", operation);
  lastUpgradeWallShownAt.set(key, Date.now());
}

export function showUpgradeWall(info: PlanLimitInfo, operation?: string): void {
  const container = document.getElementById("upgrade-wall-container");
  const messageEl = document.getElementById("upgrade-wall-message");
  const detailContent = document.getElementById("upgrade-wall-detail-content");
  const upgradeBtn = document.getElementById("upgrade-wall-upgrade");
  const manageBtn = document.getElementById("upgrade-wall-manage");
  const dismissBtn = document.getElementById("upgrade-wall-dismiss");
  const detailsEl = document.getElementById("upgrade-wall-details");

  if (!container || !messageEl) return;

  const contextualMessage =
    operation
      ? "You hit Free plan limit for " + operation + ". " + info.message
      : info.message;
  messageEl.textContent = contextualMessage;

  if (detailContent) {
    const parts: string[] = [];
    if (info.limit_key) parts.push("limit_key: " + info.limit_key);
    if (info.limit_value != null) parts.push("limit_value: " + info.limit_value);
    if (info.request_id) parts.push("request_id: " + info.request_id);
    detailContent.textContent = parts.length > 0 ? parts.join("\n") : "";
  }
  if (detailsEl) (detailsEl as HTMLDetailsElement).open = false;

  const showManage = info.action === "manage" || (info.effective_plan === "pro");
  if (manageBtn) manageBtn.style.display = showManage ? "" : "none";
  if (upgradeBtn) upgradeBtn.style.display = showManage ? "none" : "";

  const onUpgrade = (): void => {
    hideUpgradeWall();
    createCheckoutSession("annual").then((res) => openStripeUrl(res.url));
  };
  const onManage = (): void => {
    hideUpgradeWall();
    createBillingPortalSession().then((res) => openStripeUrl(res.url));
  };
  const onDismiss = async (): Promise<void> => {
    hideUpgradeWall();
    await setUpgradeWallDismissed(info.limit_key ?? "unknown", operation ?? "unknown");
  };

  upgradeBtn?.replaceWith(upgradeBtn.cloneNode(true));
  manageBtn?.replaceWith(manageBtn.cloneNode(true));
  dismissBtn?.replaceWith(dismissBtn.cloneNode(true));

  const newUpgrade = document.getElementById("upgrade-wall-upgrade");
  const newManage = document.getElementById("upgrade-wall-manage");
  const newDismiss = document.getElementById("upgrade-wall-dismiss");
  newUpgrade?.addEventListener("click", onUpgrade);
  newManage?.addEventListener("click", onManage);
  newDismiss?.addEventListener("click", () => void onDismiss());

  container?.classList.remove("upgrade-wall-hidden");
  recordUpgradeWallShown(info, operation ?? "unknown");
}

export function hideUpgradeWall(): void {
  const container = document.getElementById("upgrade-wall-container");
  container?.classList.add("upgrade-wall-hidden");
}

/**
 * Central error handler: if plan limit, show upgrade wall; else show normal error.
 * Debounces and respects session dismiss.
 */
export async function handleOperationError(
  err: unknown,
  options?: { operation?: string; statusEl?: HTMLElement; statusMessage?: string }
): Promise<void> {
  const operation = options?.operation ?? "operation";
  const statusEl = options?.statusEl ?? document.getElementById("status-message");

  if (isPlanLimitError(err, err && typeof err === "object" && "status" in (err as object) ? (err as { status: number }).status : undefined)) {
    const info = getPlanLimitInfo(err);
    const dismissed = await isUpgradeWallDismissed(info.limit_key ?? "unknown", operation);
    if (!dismissed && shouldShowUpgradeWall(info, operation)) {
      showUpgradeWall(info, operation);
    }
    if (statusEl) {
      statusEl.textContent = options?.statusMessage ?? "Plan limit reached. See upgrade banner.";
      statusEl.style.color = "#d13438";
    }
  } else {
    const msg = err instanceof Error ? err.message : String(err);
    if (statusEl) {
      statusEl.textContent = options?.statusMessage ?? "Error: " + msg;
      (statusEl as HTMLElement).style.color = "#d13438";
    }
  }
}

/**
 * Refresh ping result display (shared helper)
 */
async function refreshPingResult(): Promise<void> {
  const pingResultEl = document.getElementById("ping-result")!;

  try {
    const storedJson = await OfficeRuntime.storage.getItem(STORAGE_KEYS.LAST_PING_RESULT);
    if (storedJson) {
      const stored = JSON.parse(storedJson as string) as StoredPingResult;
      pingResultEl.textContent = JSON.stringify(stored, null, 2);
      logger.log("Taskpane", "refresh ping result", "success");
    } else {
      pingResultEl.textContent = "No ping result stored yet";
    }
  } catch (error) {
    logger.error("Taskpane", "refresh ping result failed", error);
    pingResultEl.textContent = "Error loading ping result";
  }
}

export async function signIn(): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    const session = await authSignIn();
    refreshAccountUI(session);
    try {
      const ent = await refreshEntitlementsFromService();
      setEntitlementUI({ plan: ent.plan, status: ent.status, stripe_customer_id: ent.stripe_customer_id, current_period_end: ent.current_period_end });
    } catch {
      setEntitlementUI(null);
    }
    statusEl.textContent = "Signed in successfully";
    statusEl.style.color = "#107c10";
    logger.log("Taskpane", "sign in", "success");
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    if (error instanceof AuthDialogError && error.code === "AUTH_CANCELLED") {
      // No user-facing error
      return;
    }
    logger.error("Taskpane", "sign in failed", error);
    statusEl.textContent = "Sign-in failed: " + (error instanceof Error ? error.message : "Unknown error");
    statusEl.style.color = "#d13438";
    const code = error instanceof AuthDialogError ? error.code : "";
    if (code && (code.includes("OAUTH") || code.includes("AUTH_ERROR"))) {
      showOtpFallback();
    }
  }
}

export async function signOut(): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    await authSignOut();
    clearEntitlementsCache();
    refreshAccountUI(null);
    setEntitlementUI(null);
    statusEl.textContent = "Signed out successfully";
    statusEl.style.color = "#107c10";
    logger.log("Taskpane", "sign out", "success");
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    logger.error("Taskpane", "sign out failed", error);
    statusEl.textContent = "Sign out failed: " + (error instanceof Error ? error.message : "Unknown error");
    statusEl.style.color = "#d13438";
  }
}

/** Bounded polling: entitlements/me every ~5s, up to maxAttempts or until plan is pro. */
const ENTITLEMENTS_POLL_INTERVAL_MS = 5000;
const ENTITLEMENTS_POLL_MAX_ATTEMPTS = 12;

/**
 * Open a Stripe URL (checkout or billing portal) via Office dialog or window.open fallback.
 * When dialog opens: registers DialogMessageReceived (stripe_return → refresh + close) and
 * DialogEventReceived (dialog closed → debounced refresh unless already handled).
 */
export function openStripeUrl(url: string): void {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // const dialogUrl = "https://www.analyticstrat.com/dialog/redirect.html?to=" + encodeURIComponent(url);
  const dialogUrl = origin + "/dialog/redirect.html?to=" + encodeURIComponent(url);

  if (typeof Office !== "undefined" && Office.context?.ui?.displayDialogAsync) {
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 40, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          window.open(url, "_blank");
          return;
        }
        const dialog = result.value;
        let dialogHandled = false;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: { message?: string; origin?: string } | { error?: number }) => {
          try {
            const raw = "message" in arg ? arg.message : undefined;
            if (typeof raw !== "string") return;
            const payload = JSON.parse(raw) as { type?: string; kind?: string };
            if (payload.type !== "stripe_return") return;
            const kind = payload.kind || "unknown";
            void refreshEntitlementsAndUpdateAccountUI("stripe_return:" + kind).then(() => {
              markStripeFlowHandled("message");
              dialogHandled = true;
              dialog.close();
            });
          } catch {
            // ignore parse errors
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          if (dialogHandled) return;
          if (getMillisSinceLastStripeRefresh() < 2000) return;
          void refreshEntitlementsAndUpdateAccountUI("dialog_closed");
        });
      }
    );
  } else {
    window.open(url, "_blank");
  }
}

export async function upgradeToPro(interval: "monthly" | "annual" = "monthly"): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    const session = await authStore.load();
    if (!session) {
      statusEl.textContent = "Please sign in first.";
      statusEl.style.color = "#d13438";
      setTimeout(() => { statusEl.textContent = ""; }, 3000);
      return;
    }
    markStripeFlowStarted("upgrade:" + interval);
    const response = await createCheckoutSession(interval);
    openStripeUrl(response.url);
    statusEl.textContent = "Opening checkout…";
    statusEl.style.color = "#107c10";
    setTimeout(() => { statusEl.textContent = ""; }, 2000);
    // Bounded polling: refresh entitlements every ~5s, up to 12 times or until pro (skip if already handled or last refresh < 2s)
    let attempts = 0;
    const poll = async (): Promise<void> => {
      if (attempts >= ENTITLEMENTS_POLL_MAX_ATTEMPTS) return;
      attempts++;
      await new Promise((r) => setTimeout(r, ENTITLEMENTS_POLL_INTERVAL_MS));
      if (isStripeFlowHandled() || getMillisSinceLastStripeRefresh() < 2000) {
        void poll();
        return;
      }
      try {
        const ent = await refreshEntitlementsFromService();
        recordStripeRefresh();
        setEntitlementUI({ plan: ent.plan, status: ent.status, stripe_customer_id: ent.stripe_customer_id, current_period_end: ent.current_period_end });
        if (ent.plan === "pro") return;
        void poll();
      } catch {
        void poll();
      }
    };
    void poll();
  } catch (error) {
    logger.error("Taskpane", "upgrade to Pro failed", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    statusEl.textContent = msg.includes("NOT_FOUND") ? "No Stripe customer found; upgrade first." : "Upgrade failed: " + msg;
    statusEl.style.color = "#d13438";
  }
}

export async function manageSubscription(): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    const session = await authStore.load();
    if (!session) {
      statusEl.textContent = "Please sign in first.";
      statusEl.style.color = "#d13438";
      setTimeout(() => { statusEl.textContent = ""; }, 3000);
      return;
    }
    markStripeFlowStarted("portal");
    const response = await createBillingPortalSession();
    openStripeUrl(response.url);
    statusEl.textContent = "Opening billing portal…";
    statusEl.style.color = "#107c10";
    setTimeout(() => { statusEl.textContent = ""; }, 2000);
  } catch (error) {
    logger.error("Taskpane", "manage subscription failed", error);
    const msg = error instanceof Error ? error.message : "Unknown error";
    statusEl.textContent = msg.includes("NOT_FOUND") ? "No Stripe customer found; upgrade first." : "Manage failed: " + msg;
    statusEl.style.color = "#d13438";
  }
}

/**
 * Refresh entitlements from service and update Account UI (plan, status, period end).
 * Used by Refresh License button, Stripe return message, dialog close, and focus fallback.
 */
export async function refreshEntitlementsAndUpdateAccountUI(reason: string): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    statusEl.textContent = "Subscription updated. Refreshing license…";
    statusEl.style.color = "#107c10";
    const ent = await refreshEntitlementsFromService();
    recordStripeRefresh();
    setEntitlementUI({
      plan: ent.plan,
      status: ent.status,
      stripe_customer_id: ent.stripe_customer_id,
      current_period_end: ent.current_period_end,
    });
    statusEl.textContent = ent.plan === "pro" ? "License updated: Pro" : "License refreshed.";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    logger.error("Taskpane", "refresh entitlements failed", "reason=" + reason);
    recordStripeRefresh();
    const isAuthFailure =
      error instanceof ProxyClientError &&
      (error.status === 401 || error.code === "AUTH_REQUIRED" || error.code === "AUTH_FAILED");
    const requestId =
      error instanceof ProxyClientError ? error.requestId : undefined;
    if (isAuthFailure) {
      statusEl.textContent =
        "Could not load your plan. If this keeps happening, try signing out and signing in again.";
      statusEl.style.color = "#d13438";
    } else {
      statusEl.textContent =
        "Couldn't refresh plan status" +
        (requestId ? " (request: " + requestId.substring(0, 8) + ")" : "") +
        ".";
      statusEl.style.color = "#d13438";
    }
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  }
}

export async function refreshEntitlements(): Promise<void> {
  await refreshEntitlementsAndUpdateAccountUI("refresh_button");
}

export async function pingProxy(): Promise<void> {
  const pingResultEl = document.getElementById("ping-result")!;
  pingResultEl.textContent = "Pinging proxy...";

  try {
    const response = await ping();

    // Store with metadata
    const stored: StoredPingResult = {
      ...response,
      stored_at_iso: new Date().toISOString(),
      stored_by: "taskpane",
    };

    await OfficeRuntime.storage.setItem(STORAGE_KEYS.LAST_PING_RESULT, JSON.stringify(stored));
    await refreshPingResult();
    logger.log("Taskpane", "ping proxy", "success");
  } catch (error) {
    logger.error("Taskpane", "ping proxy failed", error);
    pingResultEl.textContent = "Error: " + (error instanceof Error ? error.message : "Unknown error");
  }
}

export async function refreshPing(): Promise<void> {
  await refreshPingResult();
}

export async function authTest(): Promise<void> {
  const pingResultEl = document.getElementById("ping-result")!;
  pingResultEl.textContent = "Testing auth...";

  try {
    const response = await testAuth();
    pingResultEl.textContent = JSON.stringify(response, null, 2);
    logger.log("Taskpane", "auth test", "success");
  } catch (error) {
    logger.error("Taskpane", "auth test failed", error);
    pingResultEl.textContent = "Error: " + (error instanceof Error ? error.message : "Unknown error");
  }
}

export async function insertPingResult(): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  try {
    // Get stored ping result
    const storedJson = await OfficeRuntime.storage.getItem(STORAGE_KEYS.LAST_PING_RESULT);
    if (!storedJson) {
      statusEl.textContent = "No ping result stored. Please ping the proxy first.";
      statusEl.style.color = "#d13438";
      return;
    }

    const stored = JSON.parse(storedJson as string) as StoredPingResult;
    const proxyBaseUrl = await getProxyBaseUrlInUse();

    // Write to Excel table at active cell
    const result = await writeTable({
      target: { kind: "activeCell" },
      tableName: "ProxyPing",
      headers: ["env", "message", "now_iso", "request_id", "base_url"],
      rows: [
        [stored.env, stored.message, stored.now_iso, stored.request_id, proxyBaseUrl],
      ],
    });

    logger.log("Taskpane", "insert ping result", "success");
    const locationMsg = result.moved
      ? " (moved to avoid overwrite)"
      : " at " + result.sheetName + "!" + result.anchorA1;
    statusEl.textContent = "Inserted table " + result.tableName + locationMsg + "!";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    logger.error("Taskpane", "insert ping result failed", error);
    statusEl.textContent = "Insert failed: " + (error instanceof Error ? error.message : "Unknown error");
    statusEl.style.color = "#d13438";
  }
}

function getMarketCalendarVenue(): "NYSE" | "NASDAQ" {
  const el = document.getElementById("market-calendar-venue") as HTMLSelectElement | null;
  if (!el || el.value !== "NASDAQ") {
    return "NYSE";
  }
  return "NASDAQ";
}

function buildMarketFormula(
  fn: "IS_OPEN" | "NEXT_TRADING_DAY" | "NEXT_SESSION_OPEN" | "NEXT_SESSION_CLOSE",
  venue: "NYSE" | "NASDAQ"
): string {
  const name = "OPTLAB.MARKET." + fn;
  // Use same pattern for both venues so NEXT_TRADING_DAY uses TODAY() consistently.
  if (fn === "NEXT_TRADING_DAY") {
    return "=" + name + "(TODAY(), \"" + venue + "\")";
  }
  if (fn === "IS_OPEN") {
    return venue === "NYSE" ? "=" + name + "()" : "=" + name + "(NOW(), \"" + venue + "\")";
  }
  // NEXT_SESSION_OPEN / NEXT_SESSION_CLOSE
  return "=" + name + "(NOW(), \"" + venue + "\")";
}

/** Number format for market calendar cells: short date (1/30/26). */
const MARKET_DATE_FORMAT = "m/d/yy";
/** Number format for session open/close: date and time (1/30/26 9:30 AM). */
const MARKET_DATETIME_FORMAT = "m/d/yy h:mm AM/PM";
/** Number format for time-only (e.g. Open/Close in HOLIDAYS spill). */
const MARKET_TIME_FORMAT = "h:mm AM/PM";

export async function insertMarketIsOpen(): Promise<void> {
  const statusEl = document.getElementById("market-calendar-status")!;
  statusEl.textContent = "";
  statusEl.style.color = "";
  try {
    const venue = getMarketCalendarVenue();
    const formula = buildMarketFormula("IS_OPEN", venue);
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const formulas = fill2d(range.rowCount, range.columnCount, () => formula);
      range.formulas = formulas;
      await context.sync();
    });
    statusEl.textContent = "Inserted into selection";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Insert failed";
    statusEl.style.color = "#d13438";
    logger.error("Taskpane", "insert IS_OPEN failed", error);
  }
}

export async function insertMarketNextTradingDay(): Promise<void> {
  const statusEl = document.getElementById("market-calendar-status")!;
  statusEl.textContent = "";
  statusEl.style.color = "";
  try {
    const venue = getMarketCalendarVenue();
    const formula = buildMarketFormula("NEXT_TRADING_DAY", venue);
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const formulas = fill2d(range.rowCount, range.columnCount, () => formula);
      range.formulas = formulas;
      const formats = fill2d(range.rowCount, range.columnCount, () => MARKET_DATE_FORMAT);
      (range as Excel.Range & { numberFormat: string[][] }).numberFormat = formats;
      await context.sync();
    });
    statusEl.textContent = "Inserted into selection";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Insert failed";
    statusEl.style.color = "#d13438";
    logger.error("Taskpane", "insert NEXT_TRADING_DAY failed", error);
  }
}

export async function insertMarketNextSessionOpen(): Promise<void> {
  const statusEl = document.getElementById("market-calendar-status")!;
  statusEl.textContent = "";
  statusEl.style.color = "";
  try {
    const venue = getMarketCalendarVenue();
    const formula = buildMarketFormula("NEXT_SESSION_OPEN", venue);
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const formulas = fill2d(range.rowCount, range.columnCount, () => formula);
      range.formulas = formulas;
      const formats = fill2d(range.rowCount, range.columnCount, () => MARKET_DATETIME_FORMAT);
      (range as Excel.Range & { numberFormat: string[][] }).numberFormat = formats;
      await context.sync();
    });
    statusEl.textContent = "Inserted into selection";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Insert failed";
    statusEl.style.color = "#d13438";
    logger.error("Taskpane", "insert NEXT_SESSION_OPEN failed", error);
  }
}

export async function insertMarketNextSessionClose(): Promise<void> {
  const statusEl = document.getElementById("market-calendar-status")!;
  statusEl.textContent = "";
  statusEl.style.color = "";
  try {
    const venue = getMarketCalendarVenue();
    const formula = buildMarketFormula("NEXT_SESSION_CLOSE", venue);
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const formulas = fill2d(range.rowCount, range.columnCount, () => formula);
      range.formulas = formulas;
      const formats = fill2d(range.rowCount, range.columnCount, () => MARKET_DATETIME_FORMAT);
      (range as Excel.Range & { numberFormat: string[][] }).numberFormat = formats;
      await context.sync();
    });
    statusEl.textContent = "Inserted into selection";
    statusEl.style.color = "#107c10";
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    statusEl.textContent = error instanceof Error ? error.message : "Insert failed";
    statusEl.style.color = "#d13438";
    logger.error("Taskpane", "insert NEXT_SESSION_CLOSE failed", error);
  }
}

export async function fetchMarketHolidays(): Promise<void> {
  const statusEl = document.getElementById("status-message")!;
  statusEl.textContent = "";
  statusEl.style.color = "";

  try {
    // Phase 1: insert formula and capture formula cell address
    let formulaCellAddress: string | null = null;
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const topLeft = range.getCell(0, 0);
      topLeft.formulas = [["=OPTLAB.MARKET.HOLIDAYS()"]];
      topLeft.load("address");
      await context.sync();
      formulaCellAddress = topLeft.address;
    });

    // Phase 2: after async custom function has time to resolve, get spill range and apply formats
    const delayMs = 1500;
    await new Promise((resolve) => setTimeout(resolve, delayMs));

    await runExcel(async (context) => {
      if (!formulaCellAddress) return;
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      // Address may be "A1" or "Sheet1!$A$1"; getRange expects the range part only
      const rangePart = formulaCellAddress.includes("!") ? formulaCellAddress.split("!")[1]! : formulaCellAddress;
      const formulaCell = sheet.getRange(rangePart);
      const spillRangeOrNull = formulaCell.getSpillingToRangeOrNullObject();
      spillRangeOrNull.load(["rowCount", "columnCount", "isNullObject"]);
      await context.sync();

      const spill = spillRangeOrNull as Excel.Range & { isNullObject?: boolean };
      // Use spill range if available; otherwise format a 50×6 block from the formula cell (fallback)
      const rowCount =
        !spill.isNullObject && spillRangeOrNull.rowCount > 0 && spillRangeOrNull.columnCount >= 6
          ? spillRangeOrNull.rowCount
          : 0;
      const targetRange =
        rowCount > 0
          ? spillRangeOrNull
          : formulaCell.getResizedRange(49, 5); // 50 rows × 6 columns
      const r = rowCount > 0 ? rowCount : 50;
      if (r <= 0) return;

      // Apply format per column: each column range needs a rowCount×1 format array
      const dateCol = targetRange.getColumn(0);
      const openCol = targetRange.getColumn(4);
      const closeCol = targetRange.getColumn(5);
      const dateFormatCol = Array.from({ length: r }, () => [MARKET_DATE_FORMAT]);
      const timeFormatCol = Array.from({ length: r }, () => [MARKET_TIME_FORMAT]);
      (dateCol as Excel.Range & { numberFormat: string[][] }).numberFormat = dateFormatCol;
      (openCol as Excel.Range & { numberFormat: string[][] }).numberFormat = timeFormatCol;
      (closeCol as Excel.Range & { numberFormat: string[][] }).numberFormat = timeFormatCol;
      await context.sync();
    });

    statusEl.textContent = "Inserted =OPTLAB.MARKET.HOLIDAYS() — table will spill from selected cell.";
    statusEl.style.color = "#107c10";
    logger.log("Taskpane", "fetch market holidays", "inserted formula");
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    logger.error("Taskpane", "fetch market holidays failed", error);
    await handleOperationError(error, {
      operation: "Market Holidays",
      statusEl,
    });
  }
}

export async function insertOptionFromTaskPane(): Promise<void> {
  const statusEl = document.getElementById("option-status")!;
  const errorEl = document.getElementById("option-error")!;
  const insertBtn = document.getElementById("insert-option-btn") as HTMLButtonElement;

  // Hide previous errors
  errorEl.classList.add("option-error-hidden");
  statusEl.textContent = "";

  // Disable button while working
  insertBtn.disabled = true;
  insertBtn.textContent = "Inserting...";

  try {
    logger.log("Options", "insert option start", "");

    // Determine active mode
    const symbolModeRadio = document.getElementById("option-mode-symbol") as HTMLInputElement;
    const isSymbolMode = symbolModeRadio.checked;

    // Get OCC symbol (build from details if needed, no network)
    let occSymbol: string;
    if (isSymbolMode) {
      // From Symbol mode: read and normalize symbol (remove spaces for parse)
      const symbolInput = (document.getElementById("option-symbol-input") as HTMLInputElement).value.trim();
      if (!symbolInput) {
        throw new UserInputError("EMPTY_SYMBOL", "Please enter an OCC symbol", "symbol");
      }
      occSymbol = symbolInput.replace(/\s+/g, "");
    } else {
      // From Details mode: build canonical OCC symbol with buildOccSymbol() (no network)
      const underlying = (document.getElementById("option-details-underlying") as HTMLInputElement).value;
      const right = (document.getElementById("option-details-right") as HTMLSelectElement).value;
      const expiration = (document.getElementById("option-details-expiration") as HTMLInputElement).value;
      const strike = (document.getElementById("option-details-strike") as HTMLInputElement).value;

      // Coerce inputs (will throw UserInputError if invalid)
      let underlyingNorm: string;
      try {
        underlyingNorm = normalizeUnderlying(underlying);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Underlying must be 1-6 characters";
        throw new UserInputError("INVALID_UNDERLYING", message, "underlying");
      }

      let rightNorm: "C" | "P";
      try {
        rightNorm = normalizeSide(right);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Side must be "C", "P", "CALL", or "PUT"';
        throw new UserInputError("INVALID_RIGHT", message, "right");
      }

      let expirationNorm: string;
      try {
        expirationNorm = coerceExcelDateToYyyyMmDd(expiration);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Expiration must be a date (cell), YYYY-MM-DD, YYYYMMDD, or M/D/YY.";
        throw new UserInputError("INVALID_EXPIRATION", message, "expiration");
      }

      let strikeNorm: number;
      try {
        strikeNorm = coerceExcelNumber(strike);
        if (strikeNorm <= 0 || !isFinite(strikeNorm)) {
          throw new UserInputError("INVALID_STRIKE", "Strike must be a positive number.", "strike");
        }
      } catch (error) {
        if (error instanceof UserInputError) {
          throw error;
        }
        const message = error instanceof Error ? error.message : "Strike must be a positive number.";
        throw new UserInputError("INVALID_STRIKE", message, "strike");
      }

      // Build OCC symbol
      const symbolResult = buildOccSymbol({
        underlying: underlyingNorm,
        side: rightNorm,
        expiration: expirationNorm,
        strike: strikeNorm,
      });
      occSymbol = symbolResult.unspaced;
    }

    // Build formula (validates via parseOccSymbol; throws if invalid format)
    const formula = buildOptionInsertFormula(occSymbol);
    logger.log("Options", "insert option formula", formula);

    // Write formula to selection (matches OPTLAB.OPTIONS.OPTION semantics; Excel shows #N/A if not listed)
    await runExcel(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load(["rowCount", "columnCount"]);
      await context.sync();
      const rows = range.rowCount;
      const cols = range.columnCount;
      const formulas = fill2d(rows, cols, () => formula);
      range.formulas = formulas;
      await context.sync();
    });

    statusEl.textContent = "Inserted into selection";
    statusEl.style.color = "#107c10"; // Green success
    setTimeout(() => {
      statusEl.textContent = "";
    }, 3000);
  } catch (error) {
    // Show friendly error message
    let errorMessage = "Insert failed";
    if (error instanceof UserInputError) {
      errorMessage = error.message;
      // Optionally highlight field if field is specified
      if (error.field) {
        const symbolModeRadio = document.getElementById("option-mode-symbol") as HTMLInputElement;
        const isSymbolMode = symbolModeRadio.checked;
        const fieldEl = document.getElementById("option-" + (isSymbolMode ? "symbol" : "details") + "-" + error.field);
        if (fieldEl) {
          fieldEl.style.borderColor = "#d13438";
          setTimeout(() => {
            fieldEl.style.borderColor = "";
          }, 3000);
        }
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    errorEl.textContent = errorMessage;
    errorEl.classList.remove("option-error-hidden");
    logger.error("Options", "insert option failed", error);
  } finally {
    // Re-enable button
    insertBtn.disabled = false;
    insertBtn.textContent = "Insert";
  }
}

export async function updateOptionPreview(): Promise<void> {
  const symbolModeRadio = document.getElementById("option-mode-symbol") as HTMLInputElement;
  const isSymbolMode = symbolModeRadio.checked;

  if (isSymbolMode) {
    // Symbol mode: Show normalized input immediately (tolerant, don't parse during typing)
    const symbolInput = (document.getElementById("option-symbol-input") as HTMLInputElement).value;
    const previewEl = document.getElementById("option-symbol-preview")!;

    if (!symbolInput.trim()) {
      previewEl.textContent = "-";
      return;
    }

    try {
      // Normalize: remove all whitespace, uppercase
      let normalized = symbolInput.replace(/\s+/g, "").replace(/[\u200B-\u200D\uFEFF]/g, "").toUpperCase();
      previewEl.textContent = normalized;

      // Try to parse - only show canonical after parse succeeds (when inputs look valid)
      if (normalized.length >= 16) {
        try {
          const parsed = parseOccSymbol(symbolInput);
          previewEl.textContent = parsed.formats.unspaced + " (canonical)";
        } catch {
          // Parse failed - just show normalized (already set above)
        }
      }
    } catch {
      previewEl.textContent = "-";
    }
  } else {
    // Details mode: Show computed symbol only when "valid enough" threshold is met
    const underlying = (document.getElementById("option-details-underlying") as HTMLInputElement).value.trim();
    const right = (document.getElementById("option-details-right") as HTMLSelectElement).value;
    const expiration = (document.getElementById("option-details-expiration") as HTMLInputElement).value.trim();
    const strike = (document.getElementById("option-details-strike") as HTMLInputElement).value.trim();
    const previewEl = document.getElementById("option-details-preview")!;

    // Check "valid enough" threshold:
    // - underlying non-empty
    // - right selected
    // - expiration non-empty and passes light check (looks like date / yyyy-mm-dd / excel serial digits)
    // - strike parses to finite number
    const hasUnderlying = underlying.length > 0;
    const hasRight = right.length > 0;
    const hasExpiration = expiration.length > 0;
    const expirationLooksValid = hasExpiration && (
      /^\d{4}-\d{2}-\d{2}$/.test(expiration) || // yyyy-mm-dd
      /^\d{8}$/.test(expiration) || // yyyymmdd
      /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(expiration) || // m/d/yy or m-d-yy
      /^\d+$/.test(expiration) // excel serial digits
    );
    let strikeParses = false;
    try {
      const strikeNum = parseFloat(strike.replace(/,/g, ""));
      strikeParses = isFinite(strikeNum) && strikeNum > 0;
    } catch {
      // strikeParses stays false
    }

    if (hasUnderlying && hasRight && hasExpiration && expirationLooksValid && strikeParses) {
      // All fields valid enough - try to build symbol (no network, just basic)
      try {
        const underlyingNorm = normalizeUnderlying(underlying);
        const rightNorm = normalizeSide(right);
        const expirationNorm = coerceExcelDateToYyyyMmDd(expiration);
        const strikeNorm = coerceExcelNumber(strike);
        
        const symbolResult = buildOccSymbol({
          underlying: underlyingNorm,
          side: rightNorm,
          expiration: expirationNorm,
          strike: strikeNorm,
        });
        previewEl.textContent = symbolResult.unspaced;
      } catch {
        previewEl.textContent = "incomplete";
      }
    } else {
      // Not valid enough - show partial or incomplete
      previewEl.textContent = "incomplete";
    }
  }
}

export async function addColumns(): Promise<void> {
  const statusEl = document.getElementById("add-columns-status");
  const addBtn = document.getElementById("add-columns-btn") as HTMLButtonElement;

  if (!statusEl || !addBtn) {
    logger.error("AddColumns", "UI elements not found", "");
    return;
  }

  // Clear previous status
  statusEl.textContent = "";
  statusEl.style.color = "";

  // Disable button while running
  addBtn.disabled = true;
  const originalButtonText = addBtn.textContent;
  addBtn.textContent = "Adding...";

  try {
    // Get checkbox states
    const overwriteCheckbox = document.getElementById(
      "add-columns-overwrite"
    ) as HTMLInputElement;
    const rowsInput = document.getElementById(
      "add-columns-rows"
    ) as HTMLInputElement;

    const overwrite = overwriteCheckbox?.checked ?? false;
    const rowsFallback = Math.max(
      1,
      Math.min(500, parseInt(rowsInput?.value || "20", 10) || 20)
    );

    // Import and call addColumnsFromSelection
    const { addColumnsFromSelection } = await import("./options/addColumns");
    const result = await addColumnsFromSelection({
      overwrite,
      rowsFallback,
    });

    // Update status
    if (result.written) {
      statusEl.textContent = result.message;
      statusEl.style.color = "#107c10"; // Green success
      logger.log("AddColumns", "handler success", result.message);
    } else {
      statusEl.textContent = result.message;
      statusEl.style.color = "#d13438"; // Red error
      logger.error("AddColumns", "handler failed", result.message);
    }

    // Clear status after 5 seconds
    setTimeout(() => {
      statusEl.textContent = "";
    }, 5000);
  } catch (error) {
    logger.error("AddColumns", "handler error", error);
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    statusEl.textContent = "Error: " + errorMessage;
    statusEl.style.color = "#d13438"; // Red error
  } finally {
    // Re-enable button
    addBtn.disabled = false;
    if (originalButtonText) {
      addBtn.textContent = originalButtonText;
    }
  }
}

export async function wrapFormulas(): Promise<void> {
  const statusEl = document.getElementById("wrap-formulas-status");
  const wrapBtn = document.getElementById("wrap-formulas-btn") as HTMLButtonElement;

  if (!statusEl || !wrapBtn) {
    logger.error("WrapFormulas", "UI elements not found", "");
    return;
  }

  // Clear previous status
  statusEl.textContent = "";
  statusEl.style.color = "";

  // Disable button while running
  wrapBtn.disabled = true;
  const originalButtonText = wrapBtn.textContent;
  wrapBtn.textContent = "Wrapping...";

  try {
    // Import and call wrapSelectedFormulas
    const { wrapSelectedFormulas } = await import("./options/formulas");
    const result = await wrapSelectedFormulas();

    // Update status
    if (result.success) {
      statusEl.textContent = result.message;
      statusEl.style.color = "#107c10"; // Green success
      logger.log("WrapFormulas", "handler success", result.message);
    } else {
      statusEl.textContent = result.message;
      statusEl.style.color = "#d13438"; // Red error
      logger.error("WrapFormulas", "handler failed", result.message);
    }

    // Clear status after 5 seconds
    setTimeout(() => {
      statusEl.textContent = "";
    }, 5000);
  } catch (error) {
    logger.error("WrapFormulas", "handler error", error);
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    statusEl.textContent = "Error: " + errorMessage;
    statusEl.style.color = "#d13438"; // Red error
  } finally {
    // Re-enable button
    wrapBtn.disabled = false;
    if (originalButtonText) {
      wrapBtn.textContent = originalButtonText;
    }
  }
}

export async function refreshSelectedOptions(): Promise<void> {
  const refreshBtn = document.getElementById("refresh-selected-btn") as HTMLButtonElement;
  const progressContainer = document.getElementById("refresh-progress");
  const progressText = document.getElementById("refresh-progress-text");
  const progressBar = document.getElementById("refresh-progress-bar");
  const progressBarContainer = document.getElementById("refresh-progress-bar-container");
  const resultsText = document.getElementById("refresh-results");
  const errorsDetails = document.getElementById("refresh-errors-details");
  const errorsSummary = document.getElementById("refresh-errors-summary");
  const errorsList = document.getElementById("refresh-errors-list");

  if (!refreshBtn || !progressContainer || !progressText || !progressBar || !progressBarContainer || !resultsText || !errorsDetails || !errorsSummary || !errorsList) {
    logger.error("RefreshSelected", "UI elements not found", "");
    return;
  }

  // Clear previous state
  clearStatusMessage(progressText);
  progressBar.style.width = "0%";
  resultsText.textContent = "";
  errorsList.innerHTML = "";
  errorsDetails.classList.add("refresh-errors-hidden");

  // Disable button and show progress
  refreshBtn.disabled = true;
  const originalButtonText = refreshBtn.textContent;
  refreshBtn.textContent = "Refreshing...";
  progressContainer.classList.remove("refresh-progress-hidden");

  // Set initial status
  setStatusMessage({
    element: progressText,
    text: "Refreshing selected options…",
    kind: "info",
  });

  try {
    const result = await refreshSelectedOptionsAction((progress) => {
      // Update progress text
      const currentText = progress.currentSymbol
        ? `Refreshing ${progress.done} of ${progress.total}... (${progress.currentSymbol})`
        : `Refreshing ${progress.done} of ${progress.total}...`;
      progressText.textContent = currentText;

      // Update progress bar
      const percent = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;
      progressBar.style.width = percent + "%";

      // Update results summary
      const resultsParts: string[] = [];
      if (progress.refreshed > 0) {
        resultsParts.push(`${progress.refreshed} refreshed`);
      }
      if (progress.skipped > 0) {
        resultsParts.push(`${progress.skipped} skipped`);
      }
      if (progress.failed > 0) {
        resultsParts.push(`${progress.failed} failed`);
      }
      resultsText.textContent = resultsParts.length > 0 ? resultsParts.join(", ") : "";
    });

    // Show final results
    const finalParts: string[] = [];
    if (result.errors.length === 0) {
      finalParts.push("All entities refreshed successfully");
      setStatusMessage({
        element: progressText,
        text: "Refresh complete.",
        kind: "success",
      });
    } else {
      finalParts.push(`${result.errors.length} error(s) occurred`);
      setStatusMessage({
        element: progressText,
        text: "Refresh complete with some errors.",
        kind: "info",
      });
    }
    resultsText.textContent = finalParts.join(". ");

    // Show errors if any
    if (result.errors.length > 0) {
      errorsSummary.textContent = `Errors (${result.errors.length})`;
      errorsList.innerHTML = "";
      for (const error of result.errors) {
        const li = document.createElement("li");
        li.textContent = `${error.address} — ${error.message}`;
        errorsList.appendChild(li);
      }
      errorsDetails.classList.remove("refresh-errors-hidden");
    }

    logger.log("RefreshSelected", "handler success", "errors=" + result.errors.length);
  } catch (error) {
    logger.error("RefreshSelected", "handler error", error);
    resultsText.textContent = "Refresh failed";
    await handleOperationError(error, {
      operation: "Refresh Selected",
      statusEl: progressText,
    });
  } finally {
    // Re-enable button
    refreshBtn.disabled = false;
    if (originalButtonText) {
      refreshBtn.textContent = originalButtonText;
    }
  }
}

export async function refreshSheetOptions(): Promise<void> {
  const refreshBtn = document.getElementById("refresh-sheet-options-btn") as HTMLButtonElement;
  const progressContainer = document.getElementById("refresh-progress");
  const progressText = document.getElementById("refresh-progress-text");
  const progressBar = document.getElementById("refresh-progress-bar");
  const progressBarContainer = document.getElementById("refresh-progress-bar-container");
  const resultsText = document.getElementById("refresh-results");
  const errorsDetails = document.getElementById("refresh-errors-details");
  const errorsSummary = document.getElementById("refresh-errors-summary");
  const errorsList = document.getElementById("refresh-errors-list");

  if (!refreshBtn || !progressContainer || !progressText || !progressBar || !progressBarContainer || !resultsText || !errorsDetails || !errorsSummary || !errorsList) {
    logger.error("RefreshSheetOptions", "UI elements not found", "");
    return;
  }

  clearStatusMessage(progressText);
  progressBar.style.width = "0%";
  resultsText.textContent = "";
  errorsList.innerHTML = "";
  errorsDetails.classList.add("refresh-errors-hidden");

  refreshBtn.disabled = true;
  const originalButtonText = refreshBtn.textContent;
  refreshBtn.textContent = "Refreshing...";
  progressContainer.classList.remove("refresh-progress-hidden");

  setStatusMessage({
    element: progressText,
    text: "Refresh Sheet (Options): scanning…",
    kind: "info",
  });

  try {
    const result = await refreshSheetOptionsAction((progress) => {
      const currentText = progress.currentSymbol
        ? "Refresh Sheet (Options): Refreshing " + progress.done + " of " + progress.total + "... (" + progress.currentSymbol + ")"
        : "Refresh Sheet (Options): Refreshing " + progress.done + " of " + progress.total + "...";
      progressText.textContent = currentText;

      const percent = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;
      progressBar.style.width = percent + "%";

      const resultsParts: string[] = [];
      if (progress.refreshed > 0) {
        resultsParts.push(progress.refreshed + " refreshed");
      }
      if (progress.skipped > 0) {
        resultsParts.push(progress.skipped + " skipped");
      }
      if (progress.failed > 0) {
        resultsParts.push(progress.failed + " failed");
      }
      resultsText.textContent = resultsParts.length > 0 ? resultsParts.join(", ") : "";
    });

    if (result.noEntitiesFound) {
      setStatusMessage({
        element: progressText,
        text: "No option entities found on the active sheet.",
        kind: "info",
      });
      resultsText.textContent = "";
    } else if (result.errors.length === 0 && result.total === undefined) {
      setStatusMessage({
        element: progressText,
        text: "Cancelled.",
        kind: "info",
      });
      resultsText.textContent = "";
    } else {
      const t = result.total ?? 0;
      const r = result.refreshed ?? 0;
      const f = result.failed ?? 0;
      const s = result.skipped ?? 0;
      const summary = "Refresh Sheet (Options): Refreshed " + r + " of " + t + (f > 0 || s > 0 ? " (" + f + " failed, " + s + " skipped)." : ".");
      setStatusMessage({
        element: progressText,
        text: result.errors.length === 0 ? "Refresh Sheet (Options): Refresh complete." : "Refresh Sheet (Options): Refresh complete with some errors.",
        kind: result.errors.length === 0 ? "success" : "info",
      });
      resultsText.textContent = summary;
    }

    if (result.errors.length > 0) {
      errorsSummary.textContent = "Errors (" + result.errors.length + ")";
      errorsList.innerHTML = "";
      for (const error of result.errors) {
        const li = document.createElement("li");
        li.textContent = error.address + " — " + error.message;
        errorsList.appendChild(li);
      }
      errorsDetails.classList.remove("refresh-errors-hidden");
    }

    logger.log("RefreshSheetOptions", "handler success", "errors=" + result.errors.length);
  } catch (error) {
    logger.error("RefreshSheetOptions", "handler error", error);
    resultsText.textContent = "Refresh failed";
    await handleOperationError(error, {
      operation: "Refresh Sheet",
      statusEl: progressText,
    });
  } finally {
    refreshBtn.disabled = false;
    if (originalButtonText) {
      refreshBtn.textContent = originalButtonText;
    }
  }
}

/**
 * Format chain insert summary for display (pure helper for testing).
 * @param opts - requested, inserted, skippedNotListed, failures, listedOnly
 * @returns Summary string for chain-summary element
 */
export function formatChainSummary(opts: {
  requested: number;
  inserted: number;
  skippedNotListed?: number;
  failures?: number;
  listedOnly?: boolean;
}): string {
  const { requested, inserted, skippedNotListed, failures, listedOnly } = opts;
  const parts: string[] = [];
  parts.push("Requested: " + requested);
  parts.push("Inserted: " + inserted);
  if (listedOnly === true) {
    if (skippedNotListed !== undefined && skippedNotListed > 0) {
      parts.push("Skipped (not listed): " + skippedNotListed);
    }
    if (failures !== undefined && failures > 0) {
      parts.push("Skipped (error): " + failures);
    }
  } else {
    parts.push("Not listed: (shown as #N/A)");
  }
  return parts.join(" • ");
}

/** Throttle: base delay ms between snapshot checks (max ~5 req/s) */
const LISTED_ONLY_DELAY_BASE_MS = 200;
/** Jitter range ms added to delay */
const LISTED_ONLY_JITTER_MS = { min: 50, max: 150 };

export async function insertChain(): Promise<void> {
  const statusEl = document.getElementById("chain-status")!;
  const summaryEl = document.getElementById("chain-summary")!;
  const errorEl = document.getElementById("chain-error")!;
  const insertBtn = document.getElementById("chain-insert-btn") as HTMLButtonElement;

  // Hide previous errors and clear summary
  errorEl.classList.add("option-error-hidden");
  statusEl.textContent = "";
  summaryEl.textContent = "";

  // Read "Only insert listed" toggle (off by default)
  const listedOnly = (document.getElementById("chain-listed-only") as HTMLInputElement).checked;

  // Disable button while working
  insertBtn.disabled = true;
  insertBtn.textContent = listedOnly ? "Validating..." : "Inserting...";

  try {
    logger.log("ChainBuilder", "insert chain start", "listedOnly=" + listedOnly);

    // Read DOM inputs
    const underlyingInput = (document.getElementById("chain-underlying") as HTMLInputElement).value.trim() || "SPY";
    const expirationInput = (document.getElementById("chain-expiration") as HTMLInputElement).value.trim();
    const callCheckbox = document.getElementById("chain-right-call") as HTMLInputElement;
    const putCheckbox = document.getElementById("chain-right-put") as HTMLInputElement;
    const strikeMinInput = (document.getElementById("chain-strike-min") as HTMLInputElement).value;
    const strikeMaxInput = (document.getElementById("chain-strike-max") as HTMLInputElement).value;
    const strikeStepInput = (document.getElementById("chain-strike-step") as HTMLInputElement).value || "5";
    const spillRadio = document.getElementById("chain-mode-spill") as HTMLInputElement;
    const mode = spillRadio.checked ? "spill" : "table";

    // Normalize underlying (reuse existing normalizer)
    let underlyingNorm: string;
    try {
      underlyingNorm = normalizeUnderlying(underlyingInput);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invalid underlying symbol";
      throw new UserInputError("INVALID_UNDERLYING", message, "underlying");
    }

    // Coerce expiration
    let expirationNorm: string;
    try {
      expirationNorm = coerceExcelDateToYyyyMmDd(expirationInput);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invalid expiration date";
      throw new UserInputError("INVALID_EXPIRATION", message, "expiration");
    }

    // Parse strike numbers
    let strikeMin: number;
    let strikeMax: number;
    let strikeStep: number;
    try {
      strikeMin = coerceExcelNumber(strikeMinInput);
      if (!isFinite(strikeMin) || strikeMin <= 0) {
        throw new UserInputError("INVALID_STRIKE_MIN", "Strike minimum must be a positive number", "strikeMin");
      }
    } catch (error) {
      if (error instanceof UserInputError) {
        throw error;
      }
      throw new UserInputError("INVALID_STRIKE_MIN", "Strike minimum must be a number", "strikeMin");
    }

    try {
      strikeMax = coerceExcelNumber(strikeMaxInput);
      if (!isFinite(strikeMax) || strikeMax <= 0) {
        throw new UserInputError("INVALID_STRIKE_MAX", "Strike maximum must be a positive number", "strikeMax");
      }
    } catch (error) {
      if (error instanceof UserInputError) {
        throw error;
      }
      throw new UserInputError("INVALID_STRIKE_MAX", "Strike maximum must be a number", "strikeMax");
    }

    try {
      strikeStep = coerceExcelNumber(strikeStepInput);
      if (!isFinite(strikeStep) || strikeStep <= 0) {
        throw new UserInputError("INVALID_STRIKE_STEP", "Strike step must be a positive number", "strikeStep");
      }
    } catch (error) {
      if (error instanceof UserInputError) {
        throw error;
      }
      throw new UserInputError("INVALID_STRIKE_STEP", "Strike step must be a number", "strikeStep");
    }

    // Build rights array
    const rights: ("C" | "P")[] = [];
    if (callCheckbox.checked) {
      rights.push("C");
    }
    if (putCheckbox.checked) {
      rights.push("P");
    }
    if (rights.length === 0) {
      throw new UserInputError("INVALID_RIGHTS", "At least one right (Call or Put) must be selected", "rights");
    }

    // Get fieldKeys from checked boxes
    const fieldCheckboxes = document.querySelectorAll("#chain-fields-checklist input[type='checkbox']:checked") as NodeListOf<HTMLInputElement>;
    const fieldKeys: string[] = [];
    for (let i = 0; i < fieldCheckboxes.length; i++) {
      fieldKeys.push(fieldCheckboxes[i].value);
    }
    // Fallback to defaults if none selected
    const finalFieldKeys = fieldKeys.length > 0 ? fieldKeys : DEFAULT_CHAIN_FIELD_KEYS;

    // Generate contracts
    const contracts = buildOptionChain({
      underlying: underlyingNorm,
      expiration: expirationNorm,
      rights: rights,
      strikeMin: strikeMin,
      strikeMax: strikeMax,
      strikeStep: strikeStep,
    });

    const requestedCount = contracts.length;
    let contractsToInsert: ChainContract[] = contracts;
    let skippedNotListed = 0;
    let failures = 0;

    if (listedOnly) {
      // Pre-validate: throttle ~5 req/s, concurrency 1, keep only listed (same snapshot path as entity)
      statusEl.textContent = "Validating contracts...";
      statusEl.style.color = "#107c10";
      const listed: ChainContract[] = [];
      for (let i = 0; i < contracts.length; i++) {
        const c = contracts[i];
        try {
          const res = await getOptionSnapshot(c.occSymbol);
          if (res.ok) {
            listed.push(c);
          } else {
            failures++;
          }
        } catch (err) {
          if (err instanceof NotListedError) {
            skippedNotListed++;
          } else {
            failures++;
          }
        }
        const jitter = LISTED_ONLY_JITTER_MS.min + Math.random() * (LISTED_ONLY_JITTER_MS.max - LISTED_ONLY_JITTER_MS.min);
        await new Promise((r) => setTimeout(r, LISTED_ONLY_DELAY_BASE_MS + jitter));
        if ((i + 1) % 20 === 0) {
          statusEl.textContent = `Validating ${i + 1} of ${contracts.length}...`;
        }
      }
      contractsToInsert = listed;
    }

    const entitlements = await getEntitlements();
    const limits = getEffectiveLimits(entitlements);

    let trimMessage: string | undefined;
    let countRequestedBeforeTrim = 0;
    if (contractsToInsert.length > limits.max_chain_rows) {
      const beforeTrim = contractsToInsert.length;
      countRequestedBeforeTrim = beforeTrim;
      contractsToInsert = contractsToInsert.slice(0, limits.max_chain_rows);
      trimMessage =
        "Plan limit: inserted first " +
        limits.max_chain_rows +
        " of " +
        beforeTrim +
        " requested. Upgrade for higher limits.";
    }

    // Build diagnostics endpoint: chain-insert|<underlying>|<exp>|<mode>|req=<N>|ins=<M>|listedOnly=<0/1>|batch=<BATCH_SIZE>
    const endpoint = `chain-insert|${underlyingNorm}|${expirationNorm}|${mode}|req=${requestedCount}|ins=${contractsToInsert.length}|listedOnly=${listedOnly ? 1 : 0}|batch=${BATCH_SIZE}`;
    const opStartTime = Date.now();
    const callId = diagnosticsStore.recordCallStart("CHAIN_INSERT", endpoint);

    try {
      // Insert with progress callback
      await insertOptionChain({
        contracts: contractsToInsert,
        fieldKeys: finalFieldKeys,
        mode: mode,
        onProgress: (done, total) => {
          statusEl.textContent = `Inserting ${done} of ${total}...`;
          statusEl.style.color = "#107c10";
        },
      });

      const ms = Date.now() - opStartTime;
      diagnosticsStore.recordCallEnd(callId, {
        ok: true,
        ms: ms,
        entitlements_request_id: entitlements.request_id,
      });

      // Success message and summary
      statusEl.textContent =
        trimMessage ?? `Inserted ${contractsToInsert.length} contracts successfully`;
      statusEl.style.color = "#107c10";
      summaryEl.textContent = formatChainSummary({
        requested: requestedCount,
        inserted: contractsToInsert.length,
        skippedNotListed: listedOnly ? skippedNotListed : undefined,
        failures: listedOnly ? failures : undefined,
        listedOnly,
      });
      logger.log("ChainBuilder", "insert chain success", "contracts=" + contractsToInsert.length + " requested=" + requestedCount);

      // Plan-limit modal: once per session for chain_builder when insert was trimmed
      if (trimMessage && !wasPlanLimitDialogShownThisSession("chain_builder")) {
        setPlanLimitDialogShownThisSession("chain_builder");
        const planName = entitlements.plan === "pro" ? "Pro" : "Free";
        logger.log("ChainBuilder", "plan limit modal shown", {
          feature: "chain_builder",
          requested: countRequestedBeforeTrim,
          inserted: contractsToInsert.length,
          limit: limits.max_chain_rows,
          plan: planName,
          entitlements_request_id: entitlements.request_id,
        });
        void showPlanLimitDialog({
          feature: "chain_builder",
          requestedCount: countRequestedBeforeTrim,
          insertedCount: contractsToInsert.length,
          limit: limits.max_chain_rows,
          planName,
          allowDontShowAgainThisSession: true,
        }).then((result) => {
          if (result.action === "upgrade") {
            logger.log("ChainBuilder", "plan limit modal: upgrade clicked", { feature: "chain_builder" });
            createCheckoutSession("annual").then((res) => openStripeUrl(res.url));
          }
        });
      }

      // Clear status after 5 seconds (leave summary visible)
      setTimeout(() => {
        statusEl.textContent = "";
      }, 5000);
    } catch (insertError) {
      const ms = Date.now() - opStartTime;
      diagnosticsStore.recordCallEnd(callId, {
        ok: false,
        ms: ms,
        entitlements_request_id: entitlements.request_id,
      });
      throw insertError;
    }
  } catch (error) {
    if (isPlanLimitError(error)) {
      await handleOperationError(error, {
        operation: "Chain Builder",
        statusEl: document.getElementById("chain-status") ?? undefined,
      });
      errorEl.textContent = "Plan limit reached. See upgrade banner.";
    } else {
      let errorMessage = "Insert failed";
      if (error instanceof UserInputError) {
        errorMessage = error.message;
        if (error.field) {
          const fieldEl = document.getElementById("chain-" + error.field);
          if (fieldEl) {
            fieldEl.style.borderColor = "#d13438";
            setTimeout(() => {
              fieldEl.style.borderColor = "";
            }, 3000);
          }
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      errorEl.textContent = errorMessage;
    }
    errorEl.classList.remove("option-error-hidden");
    logger.error("ChainBuilder", "insert chain failed", error);
  } finally {
    // Re-enable button
    insertBtn.disabled = false;
    insertBtn.textContent = "Insert Chain";
  }
}

export async function updateChainPreview(): Promise<void> {
  const previewEl = document.getElementById("chain-preview-count")!;
  const warningEl = document.getElementById("chain-preview-warning")!;

  try {
    const strikeMinInput = (document.getElementById("chain-strike-min") as HTMLInputElement).value;
    const strikeMaxInput = (document.getElementById("chain-strike-max") as HTMLInputElement).value;
    const strikeStepInput = (document.getElementById("chain-strike-step") as HTMLInputElement).value || "5";
    const callCheckbox = document.getElementById("chain-right-call") as HTMLInputElement;
    const putCheckbox = document.getElementById("chain-right-put") as HTMLInputElement;

    // Parse strike inputs
    let strikeMin: number;
    let strikeMax: number;
    let strikeStep: number;

    try {
      strikeMin = parseFloat(strikeMinInput);
      strikeMax = parseFloat(strikeMaxInput);
      strikeStep = parseFloat(strikeStepInput);
    } catch {
      previewEl.textContent = "0";
      warningEl.style.display = "none";
      return;
    }

    if (!isFinite(strikeMin) || !isFinite(strikeMax) || !isFinite(strikeStep) || strikeStep <= 0 || strikeMin >= strikeMax) {
      previewEl.textContent = "0";
      warningEl.style.display = "none";
      return;
    }

    // Count rights
    let rightsCount = 0;
    if (callCheckbox.checked) rightsCount++;
    if (putCheckbox.checked) rightsCount++;

    if (rightsCount === 0) {
      previewEl.textContent = "0";
      warningEl.style.display = "none";
      return;
    }

    // Generate strikes to count
    const strikes = generateStrikes(strikeMin, strikeMax, strikeStep);
    const count = strikes.length * rightsCount;

    previewEl.textContent = String(count);

    // Show warning if count exceeds plan limit (cached only, no refresh)
    const cached = await getCachedEntitlements();
    const maxChainRows = cached?.limits?.max_chain_rows ?? 50;
    if (count > maxChainRows) {
      warningEl.style.display = "block";
    } else {
      warningEl.style.display = "none";
    }
  } catch {
    previewEl.textContent = "0";
    warningEl.style.display = "none";
  }
}
